<template>
  <div v-if="generatorSchema" class="d-flex flex-column">
    <span class="text-h6 mb-3">Options for {{ generatorSchema.name }}</span>

    <v-tabs v-model="tab" direction="horizontal" center-active disable-transition>
      <v-tab
        v-for="name in features"
        :key="name"
        :transition="false"
        :value="name"
        :data-testid="`generatorConfig.${name.toLowerCase()}-tab`"
        density="compact"
        slider-color="primary"
        >{{ readable(name) }}</v-tab
      >
    </v-tabs>
    <v-window v-model="tab" transition="">
      <v-window-item
        v-for="feature in types"
        :key="feature.name"
        :transition="false"
        :reverse-transition="false"
        :value="feature.name"
      >
        <div class="d-flex justify-space-between align-center" style="padding: 11px 0">
          <span class="text-h6">{{ readable(feature.name) }}</span>
          <v-tooltip location="top">
            <template #activator="{ props }">
              <v-btn-icon
                size="fit-content"
                icon="mdi-help-circle-outline"
                v-bind="props"
                :href="getDocLink(feature)"
                target="_blank"
              >
              </v-btn-icon>
            </template>
            <span>Open Documentation</span>
          </v-tooltip>
        </div>

        <div v-for="param in feature.parameters" :key="param.name" style="height: 100%">
          <v-textarea
            v-if="param.name === 'licenseText'"
            v-model="editableGenModel.generator.features[feature.name][param.name]"
            variant="outlined"
            :label="readable(param.name)"
          ></v-textarea>
          <v-text-field
            v-else-if="param.type === 'string'"
            v-model="editableGenModel.generator.features[feature.name][param.name]"
            :data-testid="`generatorConfig.${param.name.toLowerCase()}-input`"
            variant="outlined"
            density="compact"
            :label="readable(param.name)"
          ></v-text-field>
          <v-checkbox
            v-if="param.type === 'boolean'"
            v-model="editableGenModel.generator.features[feature.name][param.name]"
            variant="outlined"
            density="compact"
            hide-details
            color="primary"
            :label="readable(param.name)"
          ></v-checkbox>
        </div>
      </v-window-item>
    </v-window>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { Feature, Generator, GenModel } from "./types";
import { startCase } from "lodash-es";

const compProps = defineProps<{
  generatorSchema: Generator;
  genModel: GenModel;
  configTab: string | null;
}>();

const emit = defineEmits(["update:genModel", "update:configTab"]);

const editableGenModel = computed({
  get() {
    return compProps.genModel;
  },
  set(genModel: GenModel) {
    emit("update:genModel", genModel);
  },
});

const tab = computed({
  get() {
    if (compProps.configTab === null || !features.value.includes(compProps.configTab)) return null;

    return compProps.configTab;
  },
  set(tab: string | null) {
    emit("update:configTab", tab);
  },
});

const types = computed(() => {
  return compProps.generatorSchema.features;
});

const features = computed(() => {
  return compProps.generatorSchema.features.map((it) => it.name);
});

const schemaName = computed(() => {
  return compProps.generatorSchema.type.split("::")[1];
});

function readable(arg: string) {
  return startCase(arg);
}

function getDocLink(feature: Feature) {
  let docLink = "";

  switch (feature.name) {
    // GENERAL
    case "OutEventAPI":
    case "LicenseHeader":
    case "FunctionInlining":
      docLink = `https://www.itemis.com/en/products/itemis-create/documentation/user-guide/codegen_configuring_a_generator#codegen_generating_code_${feature.name.toLowerCase()}_feature`;
      break;
    //C + Java + Cpp + Python
    case "IdentifierSettings":
    case "Includes":
    case "GeneratorOptions":
    case "Naming":
    case "Tracing":
    case "GeneralFeatures":
    case "PyPackaging":
      if (schemaName.value === "java") {
        docLink = `https://www.itemis.com/en/products/itemis-create/documentation/user-guide/codegen_java_code_generator#codegen_java_${feature.name.toLowerCase()}`;
      } else {
        docLink = `https://www.itemis.com/en/products/itemis-create/documentation/user-guide/codegen_${
          schemaName.value
        }_code_generator#codegen_${schemaName.value}_${feature.name.toLowerCase()}_feature`;
      }
      break;
    default:
      docLink =
        "https://www.itemis.com/en/products/itemis-create/documentation/user-guide/codegen_configuring_a_generator#codegen_configuring_a_generator";
  }
  return docLink;
}
</script>

<style>
.v-window {
  flex-grow: 1;
}
</style>
